import { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Snackbar, SnackbarContent, Typography } from '@mui/material';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { SETTINGS } from '../Settings/tokens';
import {
  DifferencesChangedError,
  differenceLines,
  districtLabel,
  fetchCriteriaDifferences,
  formatDifferencesDate,
  personLabel,
  RESOLUTION_LABELS,
  resolveCriteriaDifferences,
  splitByStatus,
  stateLine,
  type CriteriaDifferencesRow,
  type Resolution,
} from './criteriaDifferencesApi';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type Props = {
  source?: () => Promise<CriteriaDifferencesRow[]>;
  resolve?: (target: CriteriaDifferencesRow, status: Resolution) => Promise<CriteriaDifferencesRow>;
};

type Tab = 'open' | 'resolved';

const LOAD_FAILED = 'Couldn’t load the differences. Refresh to try again.';
const RESOLVE_FAILED = 'Couldn’t save the resolution. Try again.';
const TOAST_WORDING: Record<Resolution, string> = { state_record_corrected: 'state record corrected', district_specific: 'district-specific' };

const font = { fontFamily: SETTINGS.font } as const;
const body = { ...font, fontSize: 16, lineHeight: '24px', color: SETTINGS.ink } as const;
const strong = { ...body, fontWeight: 700 } as const;
const caption = { ...font, fontSize: 13, lineHeight: '20px', color: SETTINGS.muted } as const;
const column = { display: 'flex', flexDirection: 'column' } as const;
const districtWidth = 200;
const savedWidth = 152;
const actionWidth = 196;

export function AdminCriteriaDifferencesPage({ source = fetchCriteriaDifferences, resolve = resolveCriteriaDifferences }: Props = {}) {
  const [rows, setRows] = useState<CriteriaDifferencesRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState<Tab>('open');
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async (): Promise<boolean> => {
    try {
      setRows(await source());
      setFailed(false);
      return true;
    } catch {
      setFailed(true);
      return false;
    }
  }, [source]);

  useEffect(() => {
    let cancelled = false;
    source()
      .then((result) => {
        if (!cancelled) setRows(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  const handleResolve = async (target: CriteriaDifferencesRow, status: Resolution) => {
    setBusy(target.districtId);
    try {
      const updated = await resolve(target, status);
      setRows((current) => (current ?? []).map((r) => (r.districtId === updated.districtId ? updated : r)));
      setToast(`${districtLabel(target)} marked ${TOAST_WORDING[status]}.`);
    } catch (error) {
      if (error instanceof DifferencesChangedError) {
        if (await load()) setToast(`${districtLabel(target)}’s differences changed since you loaded the page. The list is up to date now.`);
      } else {
        setToast(RESOLVE_FAILED);
      }
    } finally {
      setBusy(null);
    }
  };

  const { open, resolved } = splitByStatus(rows ?? []);
  const onOpen = tab === 'open';
  const list = onOpen ? open : resolved;

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: SETTINGS.surface }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box component="main" sx={{ flex: 1, minWidth: 0, padding: '32px 24px' }}>
        <Box sx={{ maxWidth: PAGE_MAX_WIDTH, margin: '0 auto', ...column, gap: '24px' }}>
          <Box sx={{ ...column, gap: '4px' }}>
            <Typography component="h1" sx={{ ...font, fontSize: 28, lineHeight: '36px', fontWeight: 700, color: SETTINGS.ink }}>
              Criteria differences
            </Typography>
            <Typography sx={{ ...body, color: SETTINGS.muted }}>
              Districts whose exit criteria differ from our state record. Each district is already using its own criteria.
            </Typography>
          </Box>
          <Tabs tab={tab} openCount={open.length} onTab={setTab} />
          {failed && <Typography data-testid="differences-error" sx={{ ...body, color: SETTINGS.muted }}>{LOAD_FAILED}</Typography>}
          {!failed && rows === null && <CircularProgress data-testid="differences-loading" size={24} />}
          {!failed && rows !== null && (
            <Box
              component="section"
              aria-label={onOpen ? 'Open differences' : 'Resolved differences'}
              sx={{ bgcolor: SETTINGS.cardBg, borderRadius: '8px', boxShadow: SETTINGS.cardShadow, overflow: 'hidden' }}
            >
              <Header onOpen={onOpen} />
              {list.length === 0 && (
                <Typography sx={{ ...body, color: SETTINGS.muted, padding: '32px 24px' }}>
                  {onOpen ? 'No open differences.' : 'Nothing resolved yet.'}
                </Typography>
              )}
              {list.map((row) => (
                <Row key={row.districtId} row={row} busy={busy !== null} onResolve={handleResolve} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
      <Snackbar
        open={toast !== null}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarContent message={toast ?? ''} sx={{ ...font, bgcolor: SETTINGS.ink }} />
      </Snackbar>
    </Box>
  );
}

function Tabs({ tab, openCount, onTab }: { tab: Tab; openCount: number; onTab: (tab: Tab) => void }) {
  const tabs: { id: Tab; label: string }[] = [
    { id: 'open', label: `Open · ${openCount}` },
    { id: 'resolved', label: 'Resolved' },
  ];
  return (
    <Box role="tablist" sx={{ display: 'flex', gap: '24px', borderBottom: `1px solid ${SETTINGS.divider}` }}>
      {tabs.map((t) => {
        const selected = t.id === tab;
        return (
          <Box
            key={t.id}
            component="button"
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onTab(t.id)}
            sx={{
              mb: '-1px',
              py: '8px',
              px: 0,
              border: 0,
              borderBottom: `2px solid ${selected ? SETTINGS.primary : 'transparent'}`,
              bgcolor: 'transparent',
              color: selected ? SETTINGS.primary : SETTINGS.muted,
              ...font,
              fontSize: 16,
              lineHeight: '24px',
              fontWeight: 700,
              cursor: 'pointer',
              '&:hover': { color: selected ? SETTINGS.primary : SETTINGS.ink },
            }}
          >
            {t.label}
          </Box>
        );
      })}
    </Box>
  );
}

function Header({ onOpen }: { onOpen: boolean }) {
  return (
    <Box
      data-testid="differences-header"
      sx={{ display: 'flex', gap: '16px', padding: '12px 24px', bgcolor: SETTINGS.noteBg, borderBottom: `1px solid ${SETTINGS.divider}` }}
    >
      <Typography sx={{ ...caption, width: districtWidth, flexShrink: 0 }}>District</Typography>
      <Typography sx={{ ...caption, flex: '1 1 0', minWidth: 0 }}>What’s different from our state record</Typography>
      <Typography sx={{ ...caption, width: savedWidth, flexShrink: 0 }}>Saved by</Typography>
      <Typography sx={{ ...caption, width: actionWidth, flexShrink: 0 }}>{onOpen ? '' : 'Resolution'}</Typography>
    </Box>
  );
}

function Row({ row, busy, onResolve }: { row: CriteriaDifferencesRow; busy: boolean; onResolve: (row: CriteriaDifferencesRow, status: Resolution) => void }) {
  const resolution = row.status === 'open' ? null : row.status;
  return (
    <Box
      data-testid="differences-row"
      sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px', padding: '16px 24px', '& + &': { borderTop: `1px solid ${SETTINGS.divider}` } }}
    >
      <Box sx={{ ...column, width: districtWidth, flexShrink: 0 }}>
        <Typography sx={strong}>{districtLabel(row)}</Typography>
        <Typography sx={caption}>{stateLine(row)}</Typography>
      </Box>
      <Box sx={{ ...column, flex: '1 1 0', minWidth: 0, gap: '8px' }}>
        {differenceLines(row).map((line, index) => (
          <Box key={`${line.where}-${line.what}-${index}`} sx={column}>
            <Typography sx={caption}>{line.where}</Typography>
            <Typography sx={body}>{line.what}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ ...column, width: savedWidth, flexShrink: 0 }}>
        <Typography sx={body}>{personLabel(row.savedByName, row.savedBy)}</Typography>
        <Typography sx={caption}>{formatDifferencesDate(row.savedAt)}</Typography>
      </Box>
      <Box sx={{ ...column, width: actionWidth, flexShrink: 0, gap: '8px' }}>
        {resolution ? (
          <>
            <Typography sx={strong}>{RESOLUTION_LABELS[resolution]}</Typography>
            <Typography sx={caption}>{`${personLabel(row.resolvedByName, row.resolvedBy ?? '')} · ${formatDifferencesDate(row.resolvedAt ?? '')}`}</Typography>
          </>
        ) : (
          <>
            <Button variant="outlined" onClick={() => onResolve(row, 'state_record_corrected')} disabled={busy}>
              {RESOLUTION_LABELS.state_record_corrected}
            </Button>
            <Button variant="outlined" onClick={() => onResolve(row, 'district_specific')} disabled={busy}>
              {RESOLUTION_LABELS.district_specific}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
